import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Pencil, Trash2, ScanLine, Package, RefreshCw, QrCode, ClipboardCheck } from 'lucide-react';
import { formatDate } from '../../lib/formatDate';

const TIPI_IMPIANTO = ['Registratore di cassa', 'Stampante fiscale', 'Gestionale', 'POS', 'Bilancia', 'Altro'];

function ImpiantoFormDialog({
  open, onOpenChange, editingImpianto, form, setForm,
  puntiVendita, scannedArticolo, onScanClick, onSubmit,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" className="rounded-none font-mono uppercase text-xs" data-testid="new-impianto-btn">
          <Plus className="h-3 w-3 mr-1" />
          Nuovo Impianto
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl rounded-none">
        <DialogHeader>
          <DialogTitle className="font-mono uppercase tracking-wider">
            {editingImpianto ? 'Modifica Impianto' : 'Nuovo Impianto'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label className="text-xs font-mono uppercase">Punto Vendita</Label>
            <Select
              value={form.punto_vendita_id || "none"}
              onValueChange={(value) => setForm({...form, punto_vendita_id: value === "none" ? "" : value})}
            >
              <SelectTrigger className="rounded-none" data-testid="impianto-punto">
                <SelectValue placeholder="Seleziona punto vendita" />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="none">Non assegnato</SelectItem>
                {puntiVendita.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.nome} - {p.citta}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-mono uppercase">Tipo *</Label>
              <Select value={form.tipo} onValueChange={(value) => setForm({...form, tipo: value})}>
                <SelectTrigger className="rounded-none" data-testid="impianto-tipo">
                  <SelectValue placeholder="Seleziona tipo" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {TIPI_IMPIANTO.map((tipo) => (
                    <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Matricola *</Label>
              <div className="flex gap-2">
                <Input
                  value={form.matricola}
                  onChange={(e) => setForm({...form, matricola: e.target.value})}
                  className="rounded-none font-mono flex-1"
                  required
                  data-testid="impianto-matricola"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={onScanClick}
                  className="rounded-none shrink-0"
                  title="Scansiona da magazzino"
                  data-testid="impianto-scan-btn"
                >
                  <ScanLine className="h-4 w-4" />
                </Button>
              </div>
              {scannedArticolo && (
                <div className="mt-1 text-[10px] text-emerald-600 dark:text-emerald-400 flex items-center gap-1" data-testid="impianto-scanned-info">
                  <Package className="h-3 w-3" />
                  Magazzino: {scannedArticolo.descrizione} — verrà scaricata alla creazione
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-mono uppercase">Marca</Label>
              <Input
                value={form.marca}
                onChange={(e) => setForm({...form, marca: e.target.value})}
                className="rounded-none"
                data-testid="impianto-marca"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Modello</Label>
              <Input
                value={form.modello}
                onChange={(e) => setForm({...form, modello: e.target.value})}
                className="rounded-none"
                data-testid="impianto-modello"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-mono uppercase">Data Installazione</Label>
              <Input
                type="date"
                value={form.data_installazione}
                onChange={(e) => setForm({...form, data_installazione: e.target.value})}
                className="rounded-none"
                data-testid="impianto-data-installazione"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Scadenza Verifica Periodica</Label>
              <Input
                type="date"
                value={form.data_scadenza_verifica}
                onChange={(e) => setForm({...form, data_scadenza_verifica: e.target.value})}
                className="rounded-none"
                data-testid="impianto-scadenza"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Note</Label>
            <Textarea
              value={form.note}
              onChange={(e) => setForm({...form, note: e.target.value})}
              className="rounded-none"
              data-testid="impianto-note"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">
              Annulla
            </Button>
            <Button type="submit" className="rounded-none font-mono uppercase" data-testid="save-impianto-btn">
              {editingImpianto ? 'Aggiorna' : 'Crea'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Tab "Tutti Impianti": tabella + dialog crea/modifica. Stato nel parent. */
export default function ImpiantiTab({
  clienteId, impianti, puntiVendita, isAdmin, getPuntoVenditaName,
  dialogOpen, onDialogOpenChange, editingImpianto, form, setForm,
  scannedArticolo, onScanClick, onSubmit, onEdit, onDelete, onQrAde, onSostituisci,
}) {
  const navigate = useNavigate();
  return (
    <>
      <div className="flex justify-end mb-4">
        {isAdmin && (
          <ImpiantoFormDialog
            open={dialogOpen}
            onOpenChange={onDialogOpenChange}
            editingImpianto={editingImpianto}
            form={form}
            setForm={setForm}
            puntiVendita={puntiVendita}
            scannedArticolo={scannedArticolo}
            onScanClick={onScanClick}
            onSubmit={onSubmit}
          />
        )}
      </div>

      {impianti.length === 0 ? (
        <p className="text-muted-foreground text-sm py-4">Nessun impianto registrato</p>
      ) : (
        <div className="border border-border">
          <table className="data-table">
            <thead>
              <tr>
                <th>Matricola</th>
                <th>Tipo</th>
                <th>Marca/Modello</th>
                <th>Punto Vendita</th>
                <th>Scadenza Verifica Periodica</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {impianti.map((imp) => (
                <tr key={imp.id}>
                  <td className="font-mono">{imp.matricola}</td>
                  <td>{imp.tipo}</td>
                  <td>{imp.marca} {imp.modello}</td>
                  <td>{getPuntoVenditaName(imp.punto_vendita_id)}</td>
                  <td>
                    <Badge variant="outline" className="rounded-none font-mono text-xs">
                      {formatDate(imp.data_scadenza_verifica)}
                    </Badge>
                  </td>
                  <td>
                    <div className="flex gap-1">
                      {isAdmin && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-none text-teal-600 dark:text-teal-400"
                            onClick={() => navigate(`/rapportini/nuovo?cliente_id=${clienteId}&impianto_id=${imp.id}`)}
                            title="Nuovo Rapporto di Intervento (checklist RT)"
                            data-testid={`rapportino-${imp.id}`}
                          >
                            <ClipboardCheck className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={`h-8 w-8 rounded-none ${imp.qr_ade_url || imp.qr_ade_image_b64 ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}
                            onClick={() => onQrAde(imp)}
                            title={imp.qr_ade_url || imp.qr_ade_image_b64 ? 'QR AdE salvato — visualizza/stampa' : 'QR Agenzia delle Entrate (da aggiungere)'}
                            data-testid={`qr-ade-${imp.id}`}
                          >
                            <QrCode className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-none text-cyan-600 dark:text-cyan-400"
                            onClick={() => onSostituisci(imp)}
                            title="Sostituisci cassa"
                            data-testid={`sostituisci-cassa-${imp.id}`}
                          >
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-none" onClick={() => onEdit(imp)} data-testid={`edit-impianto-${imp.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-none text-destructive" onClick={() => onDelete(imp.id)} data-testid={`delete-impianto-${imp.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
